import React, { useContext } from 'react';
import RenderAlignedHtml from '@bbkPricingPage/components/card/components/aligned-html-render';
import { PriceComponent } from '@bbkPricingPage/components/card/components/price-component';
import { PricingTableContext } from '@bbkPricingPage/context/pricing-table-context';
import {
  PricingTableHelper,
  resolvePlanQuantity,
  resolveQuantityMeta,
} from '@bbkPricingPage/helpers/pricing-table-helper';
import {
  type Discount,
  type Frequency,
  type FrequencyOrientation,
  type Subscription,
} from '@bbkPricingPage/types/pricing-table-types';
import { type ActiveItemPrice } from '../plan-customisation';

interface PlanDetailProps {
  selectedPlanPrice: ActiveItemPrice;
  frequency: Frequency;
  applicableDiscounts?: Discount[];
  subscription?: Subscription;
  priceDisplayOrientation: FrequencyOrientation | undefined;
}

const PlanDetailComponent: React.FC<PlanDetailProps> = ({
  selectedPlanPrice,
  frequency,
  applicableDiscounts,
  subscription,
  priceDisplayOrientation,
}) => {
  const { priceQuantities, quantity: initQuantity } =
    useContext(PricingTableContext);
  const selectedPlan = selectedPlanPrice.item;
  const priceSelected = selectedPlanPrice.price;
  const quantityMeta = resolveQuantityMeta(selectedPlan, priceSelected);

  const subscriptionPlan = PricingTableHelper.getSubscribedPlan(subscription);

  const planQuantity = resolvePlanQuantity({
    priceId: priceSelected.billing_system_id,
    priceQuantities,
    initQuantity,
    isSubscribedPrice: selectedPlanPrice.isSubscribedPrice,
    subscribedPlanQuantity: subscriptionPlan?.quantity,
    quantityMeta,
  });

  return (
    <div className="tw-mb-4">
      <div className="tw-flex tw-items-start tw-justify-between tw-text-pricingtable-text-base">
        <div>
          <span className="tw-inline-block tw-text-xs tw-font-medium tw-uppercase !tw-text-[var(--pricingtable-style-accent-color)]">
            PLAN
          </span>
          <div className="tw-flex tw-items-center">
            <h2 className="!tw-text-xl tw-font-bold !tw-text-[var(--pricing-page-text-font-text-color)]">
              {selectedPlan.customization.display_name ??
                selectedPlan.customization.name}
            </h2>
          </div>
          <span className="tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_60%,transparent)]">
            <RenderAlignedHtml
              value={selectedPlan.customization.description ?? ''}
            />
          </span>
        </div>
        <div className="tw-text-right">
          <PriceComponent
            id={selectedPlan.billing_system_id}
            price={priceSelected}
            quantity={planQuantity}
            frequency={frequency}
            applicableDiscounts={applicableDiscounts}
            frequencyOrientation={priceDisplayOrientation}
            className="tw-mt-4 tw-items-end tw-justify-end tw-whitespace-nowrap"
            priceClassName="tw-text-right tw-font-semibold tw-text-xl"
            suffixClassName="tw-text-right tw-font-normal tw-text-base tw-whitespace-nowrap tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_60%,transparent)]"
            ignoreEqualHeight={true}
          />
        </div>
      </div>
    </div>
  );
};

export const PlanDetail = React.memo(PlanDetailComponent);
